import { Link, useLocation, useNavigate } from 'react-router-dom';
import { memo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import {
  Filter,
  Layers,
  AlertCircle,
  Grid3X3,
  List,
  TrendingUp,
  TrendingDown,
  LogOut,
} from 'lucide-react';
import { NAV_SECTIONS, type WorkQueueNavItem } from './workQueueNav';

export type FilterMode = 'all' | 'critical' | 'raises' | 'drops';
export type ViewMode = 'grid' | 'list';

interface CountData {
  total: number;
  inWindow: number;
}

interface ModeCounts {
  all: number;
  critical: number;
  raises: number;
  drops: number;
}

interface WorkQueueProps {
  filterMode: FilterMode;
  onFilterChange: (mode: FilterMode) => void;
  viewMode: ViewMode;
  onViewModeChange: (mode: ViewMode) => void;
  counts: {
    pending: CountData;
    approved: CountData;
    rejected: CountData;
  };
  modeCounts?: ModeCounts;
}

// Doctrine: Presence Ladder - subtle hints, not red badges
// All filters use amber accent, no traffic lights
const FILTERS: Array<{
  id: FilterMode;
  label: string;
  icon: typeof Layers;
  color: string;
}> = [
  { id: 'all', label: 'All Units', icon: Layers, color: 'text-[var(--text-secondary)]' },
  { id: 'critical', label: 'Urgent', icon: AlertCircle, color: 'text-[var(--accent)]' },
  { id: 'raises', label: 'Raises', icon: TrendingUp, color: 'text-[var(--accent)]' },
  { id: 'drops', label: 'Drops', icon: TrendingDown, color: 'text-[var(--accent)]' },
];

export const WorkQueue = memo(function WorkQueue({
  filterMode,
  onFilterChange,
  viewMode,
  onViewModeChange,
  counts,
  modeCounts,
}: WorkQueueProps) {
  // Lightweight Render Counter
  if (import.meta.env.DEV && typeof window !== 'undefined') {
    const renderWindow = window as Window & { __renders?: Record<string, number> };
    renderWindow.__renders = renderWindow.__renders ?? {};
    renderWindow.__renders.WorkQueue = (renderWindow.__renders.WorkQueue ?? 0) + 1;
  }

  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const pendingInWindow = counts.pending.inWindow;
  const pendingTotal = counts.pending.total;
  const doneCount = counts.approved.inWindow;
  const isNavItemActive = (item: WorkQueueNavItem) => {
    const paths = [item.path, ...(item.aliases ?? [])];
    return paths.some(
      (path) => location.pathname === path || location.pathname.startsWith(`${path}/`),
    );
  };

  // Get count for a specific filter mode
  const getModeCount = (mode: FilterMode): number | undefined => {
    if (!modeCounts) return undefined;
    return modeCounts[mode];
  };

  // Format time estimate based on count (0.75 min per action)
  const formatTimeEstimate = (count: number): string => {
    if (count === 0) return '';
    const minutes = Math.round(count * 0.75);
    if (minutes === 0) return '~1m';
    if (minutes < 60) return `~${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `~${hours}h ${mins}m` : `~${hours}h`;
  };

  // Check if we're on Command Center main page
  const isCommandCenterMain = location.pathname === '/command';

  // Handle filter click - navigate to Command Center if on sub-page
  const handleFilterClick = (filterId: FilterMode) => {
    if (isCommandCenterMain) {
      onFilterChange(filterId);
    } else {
      // Navigate to Command Center
      navigate('/command');
    }
  };

  // Handle view mode click - navigate to Command Center if on sub-page
  const handleViewModeClick = (mode: ViewMode) => {
    if (isCommandCenterMain) {
      onViewModeChange(mode);
    } else {
      // Navigate to Command Center with the selected view mode
      // Store in sessionStorage so CommandCenter can pick it up
      sessionStorage.setItem('commandCenterViewMode', mode);
      navigate('/command');
    }
  };

  return (
    <div className="w-full md:w-44 bg-[var(--bg)] border-b md:border-b-0 md:border-r border-[var(--border-color)] flex flex-col shrink-0">
      <div className="hidden px-4 py-3 border-b border-[var(--border-color)] md:flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <Filter className="w-4 h-4 text-[var(--text-muted)]" />
          <span className="text-xs font-bold text-[var(--text-muted)] uppercase tracking-wide">
            Work Queue
          </span>
        </div>
        {pendingInWindow > 0 && (
          <span className="bg-[var(--surface-hover)] text-[var(--text-secondary)] text-xs font-bold px-2 py-0.5 rounded-full">
            {pendingInWindow}
          </span>
        )}
      </div>

      {isCommandCenterMain && (
        <div className="hidden md:block px-2 py-2 border-b border-[var(--border-color)]">
          <div className="flex rounded-lg bg-[var(--surface)] p-0.5">
            <button
              onClick={() => handleViewModeClick('grid')}
              aria-pressed={viewMode === 'grid'}
              data-view-mode="grid"
              data-active={viewMode === 'grid'}
              className={`flex-1 flex min-h-9 items-center justify-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--accent)] ${
                viewMode === 'grid'
                  ? 'bg-[var(--surface-hover)] text-[var(--text-primary)] shadow-sm'
                  : 'text-[var(--text-muted)] hover:text-[var(--text-secondary)]'
              }`}
            >
              <Grid3X3 className="w-3.5 h-3.5" />
              Grid
            </button>
            <button
              onClick={() => handleViewModeClick('list')}
              aria-pressed={viewMode === 'list'}
              data-view-mode="list"
              data-active={viewMode === 'list'}
              className={`flex-1 flex min-h-9 items-center justify-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--accent)] ${
                viewMode === 'list'
                  ? 'bg-[var(--accent)]/20 text-[var(--accent)] shadow-sm'
                  : 'text-[var(--text-muted)] hover:text-[var(--text-secondary)]'
              }`}
            >
              <List className="w-3.5 h-3.5" />
              List
            </button>
          </div>
        </div>
      )}

      <div className="overflow-x-auto md:flex-1 md:overflow-x-visible md:overflow-y-auto py-2 px-2">
        {/* Filters only shown in list view */}
        {isCommandCenterMain && viewMode === 'list' ? (
          <div className="hidden md:block space-y-1">
            {FILTERS.map((filter) => {
              const Icon = filter.icon;
              const isActive = filterMode === filter.id;
              const count = getModeCount(filter.id);

              return (
                <button
                  key={filter.id}
                  onClick={() => handleFilterClick(filter.id)}
                  className={`
                  w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-left transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--accent)]
                  ${
                    isActive
                      ? `${filter.color} bg-[var(--accent-dim)]`
                      : 'text-[var(--text-muted)] hover:bg-[var(--surface-hover)] hover:text-[var(--text-secondary)]'
                  }
                `}
                >
                  <div className="flex items-center space-x-3">
                    <Icon className="w-4 h-4" />
                    <span className="text-sm font-medium">{filter.label}</span>
                  </div>
                  {count !== undefined && count > 0 && (
                    <div className="flex flex-col items-end">
                      <span className="text-xs font-medium text-[var(--text-secondary)]">
                        {count}
                      </span>
                      <span className="text-[10px] text-[var(--text-muted)] mt-0.5">
                        {formatTimeEstimate(count)}
                      </span>
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        ) : isCommandCenterMain ? (
          <div className="hidden md:block text-center py-4 px-2">
            <div className="text-xs text-[var(--text-muted)] mb-2">
              Switch to <span className="text-[var(--accent)] font-medium">List</span> view to
              filter actions
            </div>
          </div>
        ) : null}

        {/* Navigation Links */}
        <nav
          aria-label="Primary"
          className={`mt-0 flex min-w-max items-center gap-1 md:block md:min-w-0 md:space-y-4 ${
            isCommandCenterMain ? 'md:mt-4 md:border-t md:border-[var(--border-color)] md:pt-4' : ''
          }`}
        >
          {NAV_SECTIONS.map((section) => (
            <div
              key={section.title ?? 'primary'}
              className="contents md:block md:min-w-0 md:space-y-1"
            >
              {section.title && (
                <div className="hidden md:block px-3 pb-1 text-[10px] font-bold uppercase tracking-wide text-[var(--text-tertiary)]">
                  {section.title}
                </div>
              )}
              {section.items.map((item) => {
                const Icon = item.icon;
                const isActive = isNavItemActive(item);
                const showWorkQueueBadge = item.path === '/workspace/queue' && pendingInWindow > 0;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    aria-current={isActive ? 'page' : undefined}
                    title={`${item.label}: ${item.description}`}
                    className={`shrink-0 md:w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-left transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--accent)] ${
                      isActive
                        ? 'text-[var(--accent)] bg-[var(--accent-dim)]'
                        : 'text-[var(--text-muted)] hover:bg-[var(--surface-hover)] hover:text-[var(--text-primary)]'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <Icon className="w-4 h-4" />
                      <span className="text-sm font-medium">{item.label}</span>
                    </div>
                    {showWorkQueueBadge && (
                      <span className="ml-2 inline-flex min-w-5 items-center justify-center rounded-full bg-[var(--surface-hover)] px-1.5 py-0.5 text-[10px] font-bold text-[var(--text-secondary)]">
                        {pendingInWindow}
                      </span>
                    )}
                  </Link>
                );
              })}
            </div>
          ))}
        </nav>
      </div>

      <div className="hidden md:block px-3 py-3 border-t border-[var(--border-color)]">
        {isCommandCenterMain && (
          <>
            {/* Progress Health Bar */}
            <div className="mb-2">
              <div className="flex items-center justify-between text-[10px] text-[var(--text-muted)] mb-1">
                <span>{doneCount} done</span>
                <span>{pendingInWindow} pending</span>
              </div>
              <div className="h-2 bg-[var(--surface)] rounded-full overflow-hidden flex">
                {/* Done segment - accent amber */}
                {doneCount > 0 && (
                  <div
                    className="h-full bg-[var(--accent)] transition-all duration-500"
                    style={{ width: `${(doneCount / (doneCount + pendingInWindow)) * 100}%` }}
                  />
                )}
                {/* Pending segment - muted */}
                {pendingInWindow > 0 && (
                  <div
                    className="h-full bg-[var(--surface-hover)] transition-all duration-500"
                    style={{ width: `${(pendingInWindow / (doneCount + pendingInWindow)) * 100}%` }}
                  />
                )}
              </div>
            </div>
            {/* Summary stats */}
            <div className="flex items-center justify-center gap-4 text-xs">
              <div className="flex items-center gap-1.5">
                <div className="w-2 h-2 rounded-full bg-[var(--accent)]" />
                <span className="text-[var(--text-primary)] font-medium">{doneCount}</span>
                <span className="text-[var(--text-muted)]">done</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-2 h-2 rounded-full bg-[var(--surface-hover)]" />
                <span className="text-[var(--text-primary)] font-medium">{pendingInWindow}</span>
                <span className="text-[var(--text-muted)]">pending</span>
              </div>
            </div>
            {pendingTotal > pendingInWindow && (
              <div className="text-[10px] text-[var(--text-tertiary)] text-center mt-2 pt-2 border-t border-[var(--border-subtle)]">
                {pendingTotal.toLocaleString()} total across all dates
              </div>
            )}
          </>
        )}

        {/* Logout Button */}
        <div
          className={`${isCommandCenterMain ? 'mt-3 pt-3 border-t border-[var(--border-color)]' : ''}`}
        >
          <button
            onClick={() => void logout()}
            className="w-full flex min-h-9 items-center gap-3 px-2 py-2 rounded-md transition-colors text-[var(--text-muted)] hover:bg-[var(--surface-hover)] hover:text-[var(--text-primary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--accent)]"
          >
            <LogOut className="w-3.5 h-3.5" />
            <span className="text-xs font-medium">Log out</span>
          </button>
        </div>
      </div>
    </div>
  );
});
